import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@vst/beam';

import i18n from '@app/services/translations';
import { fetchBillingInfo, FetchBillingInfoPayload, selectBillingInfo, setBoletoInfo } from '@app/redux/billingInfo';
import { resetBillingNav, Route, selectBillingNav } from '@app/redux/billingNav';
import { postGeneralMessage, scrollToTop, selectUtil, setBillingPageStatistics } from '@app/redux/utils';
import { getWorkOrders, selectWorkOrders } from '@app/redux/workOrders';
import { EventTypes } from '@app/types';

import { SettlementStatus } from '@mfe/shared/schema-types';

import BillingHome from './home';
import PaymentHistory from './payment-history';
import { UpdateBillCycleModal } from './update-bill-cycle-date/modal';
import AutoPay from './vpp/auto-pay';
import OneTimePayment from './vpp/one-time-payment';

const NavSwitch = (): JSX.Element => {
  const {
    billingInfo: { invoices },
  } = useSelector(selectBillingInfo);
  const { route, props } = useSelector(selectBillingNav);
  const dispatch = useDispatch();

  React.useLayoutEffect(() => {
    dispatch(
      postGeneralMessage({
        eventType: EventTypes.ScrollTop,
      })
    );
    dispatch(scrollToTop());
  }, [route, dispatch]);

  React.useEffect(() => {
    return () => {
      dispatch(resetBillingNav());
    };
  }, [dispatch]);

  const defaultInvoice = invoices
    ?.slice()
    ?.reverse()
    ?.find((invoice) => invoice.invoiceSettlementStatus !== SettlementStatus.Paid);
  const invoice = props.invoice ?? defaultInvoice;
  const brInvoice = invoice?.invoiceAmount?.alphabeticCode === 'BRL' ? invoice : undefined;

  switch (route) {
    case Route.Autopay:
      return <AutoPay />;
    case Route.OneTimePayment:
      return <OneTimePayment invoice={brInvoice} />;
    case Route.Payments:
      return <PaymentHistory />;
    case Route.Home:
    default:
      return <BillingHome />;
  }
};

const ManageBilling = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { toasts, removeOldestToast, addToast } = useToast();
  const { workOrders } = useSelector(selectWorkOrders);

  const {
    boletoInfo: { isError },
  } = useSelector(selectBillingInfo);

  const {
    pageStats: {
      pages: {
        billing: { lastUpdatedTime },
      },
    },
  } = useSelector(selectUtil);

  React.useEffect(() => {
    if (isError) {
      if (toasts.length) removeOldestToast();

      addToast({
        liteMode: true,
        theme: 'light',
        variant: 'error',
        body: i18n.t('Alerts:Billing.newBoletoRequestFailed.title'),
        disableAutoDismiss: true,
        onClose: () => {
          dispatch(setBoletoInfo({ isError: undefined, loading: false }));
        },
      });
    }
  }, [addToast, dispatch, isError, removeOldestToast, toasts.length]);

  React.useEffect(() => {
    return () => {
      if (toasts.length) {
        removeOldestToast();
      }

      dispatch(setBoletoInfo({ isError: undefined, loading: false }));
    };
  }, []);

  React.useEffect(() => {
    if (!lastUpdatedTime) {
      dispatch(setBillingPageStatistics());
    }

    dispatch(fetchBillingInfo(FetchBillingInfoPayload.BILLING));
  }, [dispatch, lastUpdatedTime]);

  React.useEffect(() => {
    if (workOrders === null) dispatch(getWorkOrders());
  }, []);

  return (
    <>
      <NavSwitch />
      <UpdateBillCycleModal />
    </>
  );
};

export default ManageBilling;
